// Trips (spec 35 / ATL-157): several activities grouped as one walk.
//
// CANONICAL contract. Mirrored field for field in
// packages/api/src/lib/trips-types.ts (@atlas/api has no @atlas/shared
// dependency). Change both together.
//
// Rules the payloads encode:
// - A trip is a grouping, never a merge. Every figure is summed / unioned from
//   the live member activities on read. Parked (removed) members are left out
//   and come back on restore.
// - Atlas never invents track. `joins` are the gaps between consecutive
//   members: display only, drawn dashed, never counted in distance.
// - A trip with any Strava-sourced member has no public link (Strava API
//   Policy), and is never shown to another user.
//
// Errors worth handling on clients:
//   400 { error }                                bad input (fewer than 2 activities on create, etc.)
//   404 { error }                                unknown trip / not yours / activity not yours
//   409 { error: "activity_in_other_trip", activityIds }  retry with moveFromOtherTrips: true after a confirm
//   409 { error: "trip_too_large" }              more than TRIP_MAX_ACTIVITIES members

/** Cap on members per trip (bounds the on-read geometry). */
export const TRIP_MAX_ACTIVITIES = 50;

/** A join of at least this length (m) is drawn as a dashed connector. */
export const TRIP_JOIN_MIN_DRAW_M = 20;

/** A gap of at least this many hours between members counts as a night out. */
export const TRIP_OVERNIGHT_GAP_HOURS = 6;

// Spec 38 (planned route fill). A gap is filled from the route only when both
// recorded ends are this close to it.
export const TRIP_ROUTE_SNAP_M = 100;
/** The members must cover at least this share of the route (0-100). */
export const TRIP_ROUTE_MIN_COVERAGE_PCT = 50;

export type TripLineString = { type: "LineString"; coordinates: [number, number][] };

/** One row of GET /trips. */
export interface TripSummary {
  id: string;
  name: string;
  /** First live member's start, last live member's end (ISO). Null when every member is parked. */
  startedAt: string | null;
  endedAt: string | null;
  /** Distinct Europe/London calendar days with a member. */
  days: number;
  /** Joins of TRIP_OVERNIGHT_GAP_HOURS or more. */
  nights: number;
  activityCount: number;
  distanceM: number;
  elevationGainM: number;
  /** Sum of member durations (s). */
  durationSeconds: number;
  /** Spec 38: metres filled from the planned route (0 without one). Headline distance = distanceM + filledM. */
  filledM: number;
  shareEnabled: boolean;
  /** Simplified member tracks for a list thumbnail, in order. */
  preview: TripLineString[];
}

export interface TripsResponse {
  trips: TripSummary[];
}

export interface TripMember {
  activityId: string;
  name: string;
  startedAt: string;
  endedAt: string | null;
  /** 0-based index into the trip's calendar days (Europe/London). */
  dayIndex: number;
  distanceM: number | null;
  elevationGainM: number | null;
  durationSeconds: number | null;
  sourceType: string;
  /** Strava activity id for the strava.com link (owner view only). */
  sourceId: string | null;
  /** Simplified track for the map. */
  geojson: TripLineString | null;
}

/**
 * Spec 38 round 2: a camp on a night out.
 * source: "user" = moved by hand (always wins); "gpx" = a camp waypoint
 * ("Campsite 1", "Chapel House Farm Campsite") on the planned route within
 * TRIP_CAMP_WAYPOINT_MAX_M of that night's stretch; "recording" = no better
 * evidence, the end of that evening's recording (the old behaviour).
 */
export interface TripCamp {
  latitude: number;
  longitude: number;
  name: string | null;
  source: "user" | "gpx" | "recording";
}

/** A camp waypoint counts for a night when it lies this close to that night's stretch (m). */
export const TRIP_CAMP_WAYPOINT_MAX_M = 1000;

/**
 * PUT /trips/:id/camps/:fromActivityId (the member before the night): move a
 * camp. The server snaps it to the nearest point of the route or a recording
 * within TRIP_CAMP_SNAP_M, else keeps the point as given. name: null keeps no
 * name; omitted keeps the current one. DELETE the same path = back to automatic.
 */
export interface SetTripCampInput {
  latitude: number;
  longitude: number;
  name?: string | null;
}

export interface SetTripCampResponse {
  camp: TripCamp;
}

export const TRIP_CAMP_SNAP_M = 300;

/** The gap between two consecutive members. Display geometry only. */
export interface TripJoin {
  fromActivityId: string;
  toActivityId: string;
  /** Straight-line gap, end of one to start of the next (m). */
  distanceM: number;
  gapSeconds: number;
  /** gapSeconds >= TRIP_OVERNIGHT_GAP_HOURS: a camp marker sits at `geometry` start. */
  overnight: boolean;
  /** Spec 38: true when a route fill covers this gap: no dashed connector, camp marker still shown. */
  filled: boolean;
  /** Round 2: where the night was spent; set on every overnight join, null otherwise. Draw the camp marker here. */
  camp: TripCamp | null;
  /** Two points: previous end, next start. */
  geometry: TripLineString;
}

export interface TripPeak {
  id: string;
  name: string;
  slug: string;
  elevationM: number | null;
  latitude: number;
  longitude: number;
  /** A member activity first collected it (owns the log row); false = repeat visit. */
  isNew: boolean;
}

export interface TripLandmark {
  id: string;
  name: string;
  slug: string;
  category: string;
  /** Trig pillar on a summit: folds into that peak's marker, as on the map. */
  peakId: string | null;
  latitude: number;
  longitude: number;
  isNew: boolean;
}

export interface TripTrailAlternate {
  id: string;
  name: string;
  /** The user's current coverage of the alternate (0-100), 100 when credited. */
  coveragePct: number;
}

export interface TripTrail {
  id: string;
  name: string;
  slug: string;
  /** Trail length walked by the trip's members, official alternates included (corridor-clipped, overlaps counted once). */
  kmWalked: number;
  /** Sections whose completion row points at a member activity. */
  sectionsCompleted: number;
  totalSections: number;
  /** The trail's current overall % for the user (trailProgressFractionSql), 0-100. */
  progressPct: number;
  alternates: TripTrailAlternate[];
}

export interface TripRegion {
  id: string;
  name: string;
  slug: string;
}

export interface TripDetail {
  id: string;
  name: string;
  startedAt: string | null;
  endedAt: string | null;
  days: number;
  nights: number;
  activityCount: number;
  distanceM: number;
  elevationGainM: number;
  durationSeconds: number;
  /** Sum of joins at or above TRIP_JOIN_MIN_DRAW_M that are not overnight and not filled: walked, not recorded. Never in distanceM. */
  unrecordedM: number;
  /** Spec 38: metres filled from the planned route. distanceM stays recorded only; headline = distanceM + filledM. */
  filledM: number;
  /** Spec 38: the planned route, or null. */
  route: TripRouteInfo | null;
  shareEnabled: boolean;
  shareIdentity: boolean;
  /** Any live member is Strava-sourced: no public link, Powered by Strava on the page. */
  hasStravaMember: boolean;
  /** Members parked (removed) right now; they return on restore. */
  parkedCount: number;
  createdAt: string;
}

/** Spec 38: a trip's planned route. */
export interface TripRouteInfo {
  name: string;
  distanceM: number;
  /** pending = fills not computed yet; refused = not enough recorded evidence (refusedReason says why). */
  status: "pending" | "ready" | "refused";
  refusedReason: string | null;
  /** Share of the route inside the members' corridor, 0-100. Null while pending. */
  coveragePct: number | null;
  /** The activity it came from (parked with reason trip_route), null for a direct upload. */
  sourceActivityId: string | null;
}

/**
 * Spec 38: one stretch of the planned route filling a gap between two
 * recordings that both sit on the route. Round 2: an overnight gap's fill is
 * split at the camp, so one join can have two fills (evening day, then the
 * next morning's day); sum fills by from/to when showing a join's distance. Counts like a recording (trails, peaks,
 * landmarks, regions, the exploration map), never stats totals. Drawn in its day colour, thinner and dotted ("From your route").
 */
export interface TripFill {
  dayIndex: number;
  distanceM: number;
  fromActivityId: string | null;
  toActivityId: string | null;
  geometry: TripLineString;
}

export interface TripDetailResponse {
  trip: TripDetail;
  members: TripMember[];
  /** Every join; `filled` ones draw no dashed connector (the fill replaces it) but keep their camp marker. */
  joins: TripJoin[];
  /** Spec 38: empty without a ready route. */
  fills: TripFill[];
  /** Spec 38: summits within the check-in radius of a fill that the user has not collected (isNew false). "Check in with a photo". */
  routePeaks: TripPeak[];
  peaks: TripPeak[];
  landmarks: TripLandmark[];
  trails: TripTrail[];
  regions: TripRegion[];
}

export interface CreateTripInput {
  /** Omitted = the server's default name (dominant trail, else start/end places, else dates). */
  name?: string;
  activityIds: string[];
  /** One per composed trip, reused on every retry (offline queue). */
  clientKey?: string;
  /** Move activities that already belong to another trip (after the user confirmed). */
  moveFromOtherTrips?: boolean;
}

export interface CreateTripResponse {
  tripId: string;
  /** false = the clientKey matched an earlier create; nothing new was written. */
  created: boolean;
}

export interface UpdateTripInput {
  name?: string;
  addActivityIds?: string[];
  removeActivityIds?: string[];
  moveFromOtherTrips?: boolean;
  shareEnabled?: boolean;
  shareIdentity?: boolean;
}

/** PUT /trips/:id/route: use one of the user's activities (normally a GPX upload) as the planned route. */
export interface SetTripRouteInput {
  activityId: string;
}

/** PUT /trips/:id/route and POST /trips/:id/route/upload (multipart, field `file`, a .gpx). */
export interface SetTripRouteResponse {
  route: TripRouteInfo;
}

export type TripSuggestionKind = "multi_day" | "split";

/** One activity of a suggestion, enough to judge it without leaving the card. */
export interface TripSuggestionMember {
  activityId: string;
  name: string;
  sourceType: string;
  startedAt: string;
  /** ended_at, else start + duration, else start (ISO). */
  endedAt: string;
  distanceM: number | null;
  /** 0-based index into the suggestion's calendar days (Europe/London). */
  dayIndex: number;
  /** Coarse track for a sketch. */
  geojson: TripLineString | null;
}

export interface TripSuggestion {
  /** Dismissal key. */
  firstActivityId: string;
  activityIds: string[];
  kind: TripSuggestionKind;
  startedAt: string;
  endedAt: string | null;
  days: number;
  distanceM: number;
  /** What the trip would be called if created without a name. */
  suggestedName: string;
  /** The activities, in order. */
  members: TripSuggestionMember[];
}

export interface TripSuggestionsResponse {
  suggestions: TripSuggestion[];
}

export interface TripShareUrlResponse {
  /** API landing page (OG tags). Null for a trip with a Strava member. */
  url: string | null;
  /** Web share page. Null for a trip with a Strava member. */
  pageUrl: string | null;
  /** Share image (owner only when a Strava member exists). Append &format=&style= as for activities. */
  imageUrl: string;
}

/** GET /share/trip/:id.json - public, allowlisted. No activity ids, no visit counts, no times beyond the date. */
export interface TripShareData {
  trip: {
    id: string;
    name: string;
    startedAt: string | null;
    endedAt: string | null;
    days: number;
    nights: number;
    distanceM: number;
    elevationGainM: number;
    unrecordedM: number;
    /** Spec 38. */
    filledM: number;
  };
  /** Privacy-trimmed member tracks, in order. */
  tracks: { dayIndex: number; geojson: TripLineString }[];
  /** Spec 38: route fills, privacy-trimmed like tracks. */
  fills: { dayIndex: number; distanceM: number; geometry: TripLineString }[];
  joins: {
    distanceM: number;
    overnight: boolean;
    filled: boolean;
    geometry: TripLineString;
    /** Round 2: the camp for an overnight join (null otherwise). */
    camp: { latitude: number; longitude: number; name: string | null } | null;
  }[];
  peaks: TripPeak[];
  landmarks: TripLandmark[];
  trails: { id: string; name: string; slug: string; kmWalked: number }[];
  owner: { name: string; username: string | null; level: number; avatarUrl: string | null } | null;
}
